import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Dashboard.css';
import { useUser } from '../context/UserContext';
import { CSVLink } from 'react-csv';
import Select from 'react-select';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const Dashboard = () => {
  const [centreFilter, setCentreFilter] = useState('');
  const today = new Date();
  const token = localStorage.getItem('auth-token');
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);
  const [startDateFilter, setStartDateFilter] = useState(tenDaysAgo.toISOString().split('T')[0]);
  const [endDateFilter, setEndDateFilter] = useState(today.toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeOfLeadFilter, setTypeOfLeadFilter] = useState('');
  const [productSportsFilter, setProductSportsFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { userData } = useUser();
  const { selectedCenter, setSelectedCenter } = useUser();
  const [allAcademies, setAllAcademies] = useState([]);
  const [leadStatusFilter, setLeadStatusFilter] = useState('');
  useEffect(() => {
    if (selectedCenter) {
      setCentreFilter(selectedCenter);
    } else if (userData?.userType === 'ACADEMY' && userData.academyId) {
      setCentreFilter("allAcademies");
    } else {
      setCentreFilter(null);
    }
  }, [userData, selectedCenter]);

  const handleLeadStatusChange = (e) => setLeadStatusFilter(e.target.value);
  const handleCentreChange = (e) => setCentreFilter(e.target.value);
  const handleStartDateChange = (e) => setStartDateFilter(e.target.value);
  const handleEndDateChange = (e) => setEndDateFilter(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);
  const handleTypeOfLeadChange = (e) => setTypeOfLeadFilter(e.target.value);
  const handleProductSportsChange = (e) => setProductSportsFilter(e.target.value);

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

    const fetchTrialData = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_TRIAL_VIEW, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          },
        });
  
        const data = await response.json();
  
        if (data.success && data.data.apiData && data.data.academies) {
          setAllAcademies(data.data.academies);
  
          const academiesMap = new Map(
            data.data.academies.map((academy) => [academy.id, academy.name])
          );
  
          const updatedData = data.data.apiData.map((item) => ({
            centreId: item.academyId,
            centre: academiesMap.get(item.academyId) || "Unknown Academy",
            date: item.createdAt,
            status: item.leadStatus,
            newStatus: item.status,
            typeOfLead: item.source,
            productSports: item.sportId === 11 ? "Swimming" : "Badminton",
            name: item?.name,
            mobileNumber: item.mobileNumber,
            trialdate: item.trialDate,
            createdAt: item.createdAt,
            trialTime: `${item.startTime} - ${item.endTime}`,
          }));
  
          setTableData(updatedData);
        } else {
          setError("Invalid API response structure");
        }
      } catch (error) {
        setError("Error fetching data: " + error.message);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {

    fetchTrialData();
  }, []);
  

  const filterData = (data) => {
    const searchString = searchTerm?.toLowerCase() ?? '';

    return data.filter((row) => {
      const rowDate = new Date(row.date);
      const centreMatch = !centreFilter ||
        (typeof row.centreId === 'number' && typeof centreFilter === 'string' &&
          String(row.centreId).trim().toLowerCase() === centreFilter.trim().toLowerCase());

      const startDateMatch = !startDateFilter || rowDate >= new Date(startDateFilter);
      const endDateMatch = !endDateFilter || rowDate <= new Date(endDateFilter);

      const statusMatch = !statusFilter || row.status?.toLowerCase().includes(statusFilter.toLowerCase());
      const typeOfLeadMatch = !typeOfLeadFilter || row.typeOfLead?.toLowerCase().includes(typeOfLeadFilter.toLowerCase());
      const productSportsMatch = !productSportsFilter || row.productSports?.toLowerCase().includes(productSportsFilter.toLowerCase());
      const searchMatch = (
        (row.centre?.toLowerCase() ?? '').includes(searchString) ||
        (row.date?.toLowerCase() ?? '').includes(searchString) ||
        (row.status?.toLowerCase() ?? '').includes(searchString) ||
        (row.typeOfLead?.toLowerCase() ?? '').includes(searchString) ||
        (row.productSports?.toLowerCase() ?? '').includes(searchString)
      );

      return (
        centreMatch &&
        startDateMatch &&
        endDateMatch &&
        statusMatch &&
        typeOfLeadMatch &&
        productSportsMatch &&
        searchMatch
      );
    });
  };


  useEffect(() => {
    setCurrentPage(1);
  }, [
    centreFilter,
    startDateFilter,
    endDateFilter,
    statusFilter,
    typeOfLeadFilter,
    productSportsFilter,
    leadStatusFilter,
    searchTerm
  ]);

  const headers = [
    { label: "Centre", key: "centre" },
    { label: "Mobile Number", key: "mobileNumber" },
    { label: "Name", key: "name" },
    { label: "Trial Date", key: "trialdate" },
    { label: "Trial Time", key: "trialTime" },
    { label: "Lead Status", key: "newStatus" },
    { label: "Landing Page", key: "typeOfLead" },
    { label: "Status", key: "status" }, 
    { label: "Sports", key: "productSports" }
  ];
  

  const filteredTableData = tableData.filter((row) => {
    const searchString = searchTerm.toLowerCase();
    const rowDate = new Date(row.date);
    const startDate = startDateFilter ? new Date(startDateFilter) : null;
    const endDate = endDateFilter ? new Date(endDateFilter) : null;

    const matchesAcademy = centreFilter === "allAcademies"
      ? userData.academyIds.includes(Number(row.centreId))
      : centreFilter
        ? Number(row.centreId) === Number(centreFilter)
        : true;

    const leadStatusMatch = !leadStatusFilter ||
      (row.newStatus?.toLowerCase().includes(leadStatusFilter.toLowerCase()));

    return (
      matchesAcademy &&
      (startDate ? rowDate >= startDate : true) &&
      (endDate ? rowDate <= endDate : true) &&
      (statusFilter ? row.status?.includes(statusFilter) : true) &&
      (typeOfLeadFilter ? row.typeOfLead?.includes(typeOfLeadFilter) : true) &&
      (productSportsFilter ? row.productSports?.includes(productSportsFilter) : true) &&
      leadStatusMatch &&
      (
        (row.centre?.toLowerCase() ?? '').includes(searchString) ||
        (row.name?.toLowerCase() ?? '').includes(searchString) || 
        (row.mobileNumber?.toLowerCase() ?? '').includes(searchString) || 
        (row.status?.toLowerCase() ?? '').includes(searchString) ||
        (row.typeOfLead?.toLowerCase() ?? '').includes(searchString) ||
        (row.productSports?.toLowerCase() ?? '').includes(searchString) ||
        (row.date ? row.date.toLowerCase().includes(searchString) : false) ||
        (row.mobileNumber ? String(row.mobileNumber).includes(searchString) : false)
      )
      
    );
  });


  const handleSort = (columnKey) => {
    let direction = 'ascending';


    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;

    if (!key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };
  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const sortedData = sortData(filteredTableData);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };
  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };
  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const totalEntries = filteredTableData.length;
  const indexOfFirstEntry = (currentPage - 1) * rowsPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * rowsPerPage, totalEntries);
  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };
  const getUniqueValues = (key) => {
    return [...new Set(tableData.map((row) => row[key]))];
  };

  const prepareCombinedPieChartData = (data) => {
    const typeOfLeadCounts = {};
    data.forEach((row) => {
      if (row.typeOfLead) {
        typeOfLeadCounts[row.typeOfLead] = (typeOfLeadCounts[row.typeOfLead] || 0) + 1;
      }
    });

    return {
      labels: Object.keys(typeOfLeadCounts),
      datasets: [
        {
          data: Object.values(typeOfLeadCounts),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
          ],
        },
      ],
    };
  };
  const prepareStatusPieChartData = (data) => {
    const statusData = {};

    data.forEach((row) => {
      if (!statusData[row.newStatus]) {
        statusData[row.newStatus] = {};
      }
      if (!statusData[row.newStatus][row.typeOfLead]) {
        statusData[row.newStatus][row.typeOfLead] = 0;
      }
      statusData[row.newStatus][row.typeOfLead] += 1;
    });

    const pieCharts = Object.keys(statusData).map((status) => {
      const typeOfLeadData = statusData[status];
      return {
        status,
        labels: Object.keys(typeOfLeadData),
        datasets: [
          {

            data: Object.values(typeOfLeadData),
            backgroundColor: [
              '#FF6384',
              '#36A2EB',
              '#FFCE56',
              '#4BC0C0',
            ],
          },
        ],
      };
    });

    return pieCharts;
  };

  const combinedPieChartData = prepareCombinedPieChartData(filteredTableData);
  const statusPieChartsData = prepareStatusPieChartData(filteredTableData);

  return (
    <div className="enrollment-container">
       <div className="sales-filters-login">

        <div className="floating-select">
          <select
            name="academy"
            value={centreFilter}
            onChange={(e) => setCentreFilter(e.target.value)}
            className={`sales-filter-academy ${centreFilter ? "has-value" : ""}`}
          >
            {userData?.userType === "ACADEMY" ? (
              <>
                <option value="allAcademies">All Academies</option>
                {userData.academyIds.map((academyId) => {
                  const academy = allAcademies.find((item) => item.id === academyId);
                  if (academy) {
                    return (
                      <option key={academyId} value={academyId}>
                        {academy.name}
                      </option>
                    );
                  }
                  return null;
                })}
              </>
            ) : (
              <>
                <option value="">All Academies</option>
                {allAcademies.map((academy) => (
                  <option key={academy.id} value={academy.id}>
                    {academy.name}
                  </option>
                ))}
              </>
            )}
          </select>
          {centreFilter && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select">
          <select
            className={`sales-filter-academy ${statusFilter ? 'has-value' : ''}`}
            value={statusFilter}
            onChange={handleStatusChange}
          >
            <option value="">Lead Status</option>
            {getUniqueValues('status').filter(status => status).map((status, index) => (
              <option key={index} value={status}>
                {status}
              </option>
            ))}
          </select>
          {statusFilter && <label htmlFor="statusFilter">Status</label>}
        </div>

        <div className="floating-select">
  <select
    id="typeOfLeadFilter"
    className={`sales-filter-academy ${typeOfLeadFilter ? 'has-value' : ''}`}
    value={typeOfLeadFilter}
    onChange={handleTypeOfLeadChange}
  >
    <option value="">All Pages</option>
    {getUniqueValues('typeOfLead').filter(type => type).map((type, index) => (
      <option key={index} value={type}>
        {type}
      </option>
    ))}
  </select>
  

  {typeOfLeadFilter && <label htmlFor="typeOfLeadFilter">Page</label>}
</div>


        <div className="floating-select">
          <select
            className={`sales-filter-academy ${productSportsFilter ? 'has-value' : ''}`}
            value={productSportsFilter}
            onChange={handleProductSportsChange}
          >
            <option value="">All Sports</option>
            {getUniqueValues('productSports').filter(sport => sport).map((sport, index) => (
              <option key={index} value={sport}>
                {sport}
              </option>
            ))}
          </select>
          {productSportsFilter && <label htmlFor="productSportsFilter">Sport</label>}
        </div>
        <div className="floating-select">
          <select
            className={`sales-filter-academy ${leadStatusFilter ? 'has-value' : ''}`}
            value={leadStatusFilter}
            onChange={handleLeadStatusChange}
          >
            <option value="">All Status</option>
            {getUniqueValues('newStatus').filter(status => status).map((status, index) => (
              <option key={index} value={status}>
                {status}
              </option>
            ))}
          </select>
          {leadStatusFilter && <label htmlFor="leadStatusFilter">Lead Status</label>}
        </div>
        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${startDateFilter ? 'has-value' : ''}`}
            value={startDateFilter}
            onChange={handleStartDateChange}
            onClick={(e) => e.target.showPicker()}
          />
          {startDateFilter && <label htmlFor="startDateFilter">Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${endDateFilter ? 'has-value' : ''}`}
            value={endDateFilter}
            onChange={handleEndDateChange}
            onClick={(e) => e.target.showPicker()}
          />
          {endDateFilter && <label htmlFor="endDateFilter">End Date</label>}
        </div>

      </div>
      {(combinedPieChartData.datasets?.[0]?.data?.length > 0 || statusPieChartsData.some(chart => chart.datasets?.[0]?.data?.length > 0)) && (
        <div className="pie-charts-container">
          {combinedPieChartData.datasets?.[0]?.data?.length > 0 && (
            <div className="pie-chart-container">
              <h3>Overall Type of Lead Distribution</h3>
              <Pie
                data={combinedPieChartData}
                options={{
                  plugins: {
                    datalabels: {
                      color: '#fff',
                      formatter: (value) => `${value}%`,
                    },
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
              />
            </div>
          )}

          {statusPieChartsData.map((chartData, index) =>
            chartData.datasets?.[0]?.data?.length > 0 ? (
              <div key={index} className="pie-chart">
                <h3>{chartData.status}</h3>
                <Pie
                  data={{
                    labels: chartData.labels,
                    datasets: chartData.datasets,
                  }}
                  options={{
                    plugins: {
                      datalabels: {
                        color: '#fff',
                        formatter: (value) => `${value}%`,
                      },
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              </div>
            ) : null
          )}
        </div>
      )}

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearch}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <CSVLink
          data={filteredTableData}
          headers={headers}
          filename="AllLeads.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button" >
            <span
              className="button__text"

            >
              Export CSV
            </span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>

      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchTrialData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredTableData.length === 0 && (
        <div className="error-message">No data available </div>
      )}
      
      <div className="payment-list-scroll-container" style={{  overflowY: 'auto' }}>
        <table className="payment-list-table">
        <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, index) => (
              <tr key={index}>
                <td>{row.centre}</td>
                <td>{row.mobileNumber}</td>
                <td>{row.name}</td>
                <td>{new Date(row.trialdate).toISOString().split('T')[0]}</td>
                <td>{row.trialTime}</td>
                <td>{row.status}</td>
                <td>{row.typeOfLead}</td>
                <td>{row.newStatus}</td>
                <td>{row.productSports}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Dashboard;